/**
 * Phase 10.1E — WhatsApp Intake Progress + Admin Visibility (read-only).
 *
 * Reads from `lead.whatsapp_form_intake` (set by /app/backend/crm_wa_intake.py)
 * and `lead.whatsapp_auto_form_link` (set by /app/backend/crm_auto_form_link.py)
 * and surfaces the live state of the one-by-one WhatsApp intake on the CRM
 * lead detail page. No backend writes. No state-machine touching. No new
 * automation. Pure visibility for ops.
 */

import React from "react";
import {
  ChatCircleDots, CheckCircle, Pause, XCircle, Hourglass, ArrowRight,
  WarningCircle, EnvelopeOpen, PaperPlaneTilt,
} from "@phosphor-icons/react";

// Source of truth — mirror of crm_wa_intake.STEP_KEYS_ORDERED.
// Keep in sync when steps are added/removed/renamed.
const STEP_ORDER = [
  { key: "registration_region",  label: "Registration Region",  required: false, helper: true  },
  { key: "registration_number",  label: "Registration Number",  required: false, helper: false },
  { key: "make",                 label: "Brand",                required: true,  helper: false },
  { key: "model",                label: "Model",                required: true,  helper: false },
  { key: "variant",              label: "Variant",              required: false, helper: false },
  { key: "fuel_type",            label: "Fuel Type",            required: true,  helper: false },
  { key: "year",                 label: "Year",                 required: true,  helper: false },
  { key: "transmission",         label: "Transmission",         required: true,  helper: false },
  { key: "km_driven",            label: "Kilometres Driven",    required: true,  helper: false },
  { key: "owners",               label: "Owners",               required: true,  helper: false },
  { key: "hypothecation",        label: "Hypothecation",        required: true,  helper: false },
  { key: "accident_history",     label: "Accident History",     required: true,  helper: false },
  { key: "service_history",      label: "Service History",      required: true,  helper: false },
  { key: "photos",               label: "Photos",               required: false, helper: false },
  { key: "customer_intent",      label: "Customer Intent",      required: true,  helper: false },
  { key: "customer_intent_note", label: "Customer Intent Note", required: false, helper: false },
  { key: "expected_price",       label: "Expected Price",       required: true,  helper: false },
];

const TOTAL_STEPS = STEP_ORDER.length; // 17

// Two-tier waiting/stuck thresholds per Phase 10.1E spec.
// • Waiting: last outbound automation >15 min old, no fresh reply
// • Stuck:   last outbound automation >60 min old, no fresh reply
const WAITING_MS = 15 * 60 * 1000;   // 15 minutes
const STUCK_MS   = 60 * 60 * 1000;   // 60 minutes

function safeIso(s) {
  if (!s) return null;
  try { return new Date(s); } catch { return null; }
}

function fmtTs(s) {
  if (!s) return "—";
  const d = safeIso(s);
  if (!d || isNaN(d.getTime())) return "—";
  const iso = d.toISOString();
  return iso.replace("T", " ").slice(0, 16) + "Z";
}

function relativeTime(s) {
  if (!s) return "";
  const d = safeIso(s);
  if (!d || isNaN(d.getTime())) return "";
  const ms = Date.now() - d.getTime();
  if (ms < 0) return "just now";
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d2 = Math.floor(h / 24);
  return `${d2}d ago`;
}

function isCaptured(v) {
  if (v === null || v === undefined) return false;
  if (typeof v === "string" && v.trim() === "") return false;
  return true;
}

function formatValue(key, v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (key === "expected_price" && typeof v === "number") return `₹ ${v.toLocaleString("en-IN")}`;
  if (key === "km_driven" && typeof v === "number") return `${v.toLocaleString("en-IN")} km`;
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function lastInboundTs(lead) {
  const conv = Array.isArray(lead?.conversation) ? lead.conversation : [];
  for (let i = conv.length - 1; i >= 0; i -= 1) {
    if (conv[i]?.direction === "in") return conv[i].timestamp || conv[i].received_at || null;
  }
  return null;
}

function lastOutboundAutomationTs(lead) {
  const conv = Array.isArray(lead?.conversation) ? lead.conversation : [];
  for (let i = conv.length - 1; i >= 0; i -= 1) {
    const m = conv[i];
    if (m?.direction !== "out") continue;
    const mt = (m?.wa_intake_meta?.message_type) ||
               (m?.auto_form_link_meta?.message_type) ||
               (m?.auto_fallback_meta?.message_type) ||
               null;
    if (mt) return m.timestamp || m.sent_at || null;
  }
  return null;
}

// ════════════════════════════════════════════════════════════════════════════
// Chip computation
// ════════════════════════════════════════════════════════════════════════════
function computeChip(lead) {
  const autom    = (lead?.automation_state || "").trim();
  const fi       = lead?.whatsapp_form_intake || {};
  const formLink = lead?.whatsapp_auto_form_link || {};
  const status   = (lead?.status || "").toLowerCase();
  const formSubmitted = !!lead?.form_submitted;

  // Terminal lead statuses outrank everything else.
  if (status === "purchased")      return { label: "Purchased",            tone: "gold",  icon: CheckCircle };
  if (status === "lost")           return { label: "Lost",                 tone: "red",   icon: XCircle };
  if (status === "spam")           return { label: "Spam",                 tone: "red",   icon: XCircle };
  if (status === "duplicate")      return { label: "Duplicate",            tone: "muted", icon: XCircle };

  // 10.1D-owned states.
  if (autom === "website_form_submitted") {
    return { label: "Website form submitted", tone: "green", icon: CheckCircle };
  }
  if (autom === "not_available_or_sold") {
    return { label: "Sold / not available", tone: "red",   icon: XCircle };
  }
  if (autom === "wa_intake_paused") {
    return { label: "Paused",               tone: "amber", icon: Pause };
  }
  if (autom === "wa_intake_needs_admin_review") {
    return { label: "Needs admin review",   tone: "amber", icon: WarningCircle };
  }
  if (autom === "whatsapp_form_intake_complete") {
    return { label: "Ready for admin review", tone: "green", icon: CheckCircle };
  }
  if (autom === "whatsapp_form_intake_in_progress") {
    const captured = fi.captured || {};
    const n = STEP_ORDER.filter(s => isCaptured(captured[s.key])).length;
    return {
      label: `Intake in progress: ${n} / ${TOTAL_STEPS}`,
      tone: "sky",
      icon: ChatCircleDots,
    };
  }
  if (autom === "awaiting_sale_confirmation") {
    return { label: "Awaiting sale confirmation", tone: "sky", icon: Hourglass };
  }

  // 10.1B fallback ran (post 30-min) but state not awaiting yet (unlikely).
  if (formLink.fallback_sent_at && !autom) {
    return { label: "Soft follow-up sent", tone: "sky", icon: PaperPlaneTilt };
  }
  // 10.1A — form-link template was sent.
  if (formLink.form_link_sent_at && !formSubmitted) {
    return { label: "Form link sent",      tone: "sky", icon: EnvelopeOpen };
  }
  // Website form was submitted (either via web OR finalized intake).
  if (formSubmitted) {
    return { label: "Form submitted",      tone: "green", icon: CheckCircle };
  }
  // Default — no automation has touched the lead yet.
  return { label: "Waiting for form",      tone: "muted", icon: Hourglass };
}

const TONE_CLASSES = {
  green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  sky:   "bg-sky-500/15 text-sky-300 border-sky-500/30",
  amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  red:   "bg-red-500/15 text-red-300 border-red-500/30",
  gold:  "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  muted: "bg-white/5 text-white/55 border-white/10",
};

function Chip({ chip }) {
  const Icon = chip.icon;
  const cls = TONE_CLASSES[chip.tone] || TONE_CLASSES.muted;
  return (
    <span
      data-testid="wa-intake-progress-chip"
      data-chip-tone={chip.tone}
      data-chip-label={chip.label}
      className={`inline-flex items-center gap-2 px-3 py-1.5 border text-[11px] font-display tracking-widest uppercase font-bold ${cls}`}
    >
      <Icon size={14} weight="bold" />
      {chip.label}
    </span>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Main panel
// ════════════════════════════════════════════════════════════════════════════
export default function WhatsAppIntakePanel({ lead }) {
  if (!lead) return null;

  const fi       = lead.whatsapp_form_intake || {};
  const formLink = lead.whatsapp_auto_form_link || {};
  const autom    = (lead.automation_state || "").trim();
  const captured = fi.captured || {};
  const chip     = computeChip(lead);

  const currentStepKey = fi.current_step || null;
  const currentStep    = STEP_ORDER.find(s => s.key === currentStepKey) || null;

  const capturedCount = STEP_ORDER.filter(s => isCaptured(captured[s.key])).length;

  const missingRequired = STEP_ORDER.filter(
    (s) => s.required && !isCaptured(captured[s.key]),
  );
  const skippedOptional = STEP_ORDER.filter(
    (s) => !s.required && !s.helper && captured[s.key] === "" /* explicit skip = empty string */,
  );

  // Show captured rows in step-order so admins can read the lead's car details
  // at a glance.
  const capturedRows = STEP_ORDER
    .filter(s => isCaptured(captured[s.key]))
    .map((s) => ({ key: s.key, label: s.label, value: formatValue(s.key, captured[s.key]) }));

  const inboundTs    = lastInboundTs(lead);
  const outAutomTs   = lastOutboundAutomationTs(lead);

  // Two-tier waiting/stuck — only fires while automation is actively waiting
  // for a customer reply (intake-in-progress or awaiting sale confirmation).
  const isAutomationActive =
    autom === "whatsapp_form_intake_in_progress" ||
    autom === "awaiting_sale_confirmation";
  let waitTier = null; // null | "waiting" | "stuck"
  if (isAutomationActive && outAutomTs) {
    const outDate = safeIso(outAutomTs);
    const inDate  = safeIso(inboundTs);
    const noReplyAfter = !inDate || (outDate && inDate.getTime() < outDate.getTime());
    if (outDate && noReplyAfter) {
      const age = Date.now() - outDate.getTime();
      if (age >= STUCK_MS) waitTier = "stuck";
      else if (age >= WAITING_MS) waitTier = "waiting";
    }
  }

  // Lead source — Phase 10.1E spec asks to surface WhatsApp Cloud vs CTA
  // WhatsApp vs website form. Derive from existing lead fields without
  // inventing new backend writes.
  const sourceLabel = (() => {
    // Phase 10.1F — prefer explicit details_source when set.
    const ds = (lead?.details_source || "").toLowerCase();
    if (ds === "website_form") return "Website form (post-WhatsApp link)";
    if (ds === "whatsapp_intake") return "WhatsApp Cloud (one-by-one intake)";
    if (ds === "mixed")        return "Mixed (website + WhatsApp)";
    if (formLink.form_link_sent_at)      return "WhatsApp Cloud (CTA inbound)";
    if (lead?.source === "whatsapp_intake") return "WhatsApp Cloud (one-by-one intake)";
    if (lead?.form_submitted)            return "Website form";
    const ch = (lead?.channel || lead?.source || "").toLowerCase();
    if (ch.includes("whatsapp"))         return "WhatsApp Cloud";
    if (ch.includes("web") || ch === "website") return "Website form";
    return ch ? ch : "—";
  })();

  const readyForAdminReview =
    !!lead.ready_for_admin_review ||
    autom === "whatsapp_form_intake_complete" ||
    autom === "wa_intake_needs_admin_review";

  return (
    <div
      className="border border-white/10 bg-[#0E0E0E]/60 p-4 space-y-4"
      data-testid="crm-wa-intake-panel"
    >
      {/* Header row — chip + ready-for-review flag */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] text-white/45 tracking-widest uppercase font-display mb-1">
            WhatsApp Intake
          </div>
          <Chip chip={chip} />
        </div>
        {readyForAdminReview && (
          <span
            data-testid="wa-intake-ready-flag"
            className="inline-flex items-center gap-2 px-3 py-1.5 border text-[11px] font-display tracking-widest uppercase font-bold bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
          >
            <CheckCircle size={14} weight="bold" /> Ready for admin review
          </span>
        )}
        {waitTier === "waiting" && (
          <span
            data-testid="wa-intake-waiting-flag"
            data-wait-tier="waiting"
            title={`Last automated message ${relativeTime(outAutomTs)}; no customer reply since.`}
            className="inline-flex items-center gap-2 px-3 py-1.5 border text-[11px] font-display tracking-widest uppercase font-bold bg-sky-500/15 text-sky-300 border-sky-500/30"
          >
            <Hourglass size={14} weight="bold" /> Waiting · {relativeTime(outAutomTs)}
          </span>
        )}
        {waitTier === "stuck" && (
          <span
            data-testid="wa-intake-stuck-flag"
            data-wait-tier="stuck"
            title={`Last automated message ${relativeTime(outAutomTs)}; no customer reply since.`}
            className="inline-flex items-center gap-2 px-3 py-1.5 border text-[11px] font-display tracking-widest uppercase font-bold bg-amber-500/15 text-amber-300 border-amber-500/30"
          >
            <WarningCircle size={14} weight="bold" /> Stuck · needs follow-up · {relativeTime(outAutomTs)}
          </span>
        )}
      </div>

      {/* Quick stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <Stat label="Automation state" testId="wa-intake-state">
          <span className="text-white/85 font-mono text-[11px]" data-state={autom || "none"}>
            {autom || "—"}
          </span>
        </Stat>
        <Stat label="Current step" testId="wa-intake-current-step">
          <span className="text-white/85" data-step={currentStepKey || "none"}>
            {currentStep ? currentStep.label : "—"}
          </span>
        </Stat>
        <Stat label="Progress" testId="wa-intake-progress-count">
          <span className="text-white/85 font-mono">
            {capturedCount} / {TOTAL_STEPS}
          </span>
        </Stat>
        <Stat label="Status" testId="wa-intake-status">
          <span className="text-white/85">{fi.status || "—"}</span>
        </Stat>
        <Stat label="Source" testId="wa-intake-source">
          <span className="text-white/85" data-source={sourceLabel}>{sourceLabel}</span>
        </Stat>
        <Stat label="Last customer reply" testId="wa-intake-last-inbound">
          <span className="text-white/85" title={fmtTs(inboundTs)}>
            {inboundTs ? relativeTime(inboundTs) : "—"}
          </span>
        </Stat>
        <Stat label="Last automation msg" testId="wa-intake-last-outbound">
          <span className="text-white/85" title={fmtTs(outAutomTs)}>
            {outAutomTs ? relativeTime(outAutomTs) : "—"}
          </span>
        </Stat>
        <Stat label="Form-link sent" testId="wa-intake-formlink-sent">
          <span className="text-white/85" title={fmtTs(formLink.form_link_sent_at)}>
            {formLink.form_link_sent_at ? relativeTime(formLink.form_link_sent_at) : "—"}
          </span>
        </Stat>
        <Stat label="Soft follow-up sent" testId="wa-intake-fallback-sent">
          <span className="text-white/85" title={fmtTs(formLink.fallback_sent_at)}>
            {formLink.fallback_sent_at ? relativeTime(formLink.fallback_sent_at) : "—"}
          </span>
        </Stat>
      </div>

      {/* Captured fields list */}
      <div>
        <div className="text-[10px] text-white/45 tracking-widest uppercase font-display mb-2">
          Captured fields ({capturedRows.length})
        </div>
        {capturedRows.length === 0 ? (
          <div className="text-white/45 text-xs italic" data-testid="wa-intake-captured-empty">
            Nothing captured yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-xs" data-testid="wa-intake-captured-list">
            {capturedRows.map((r) => (
              <div key={r.key} className="flex items-baseline justify-between gap-2 border-b border-white/5 pb-1">
                <span className="text-white/55 uppercase tracking-wider text-[10px] font-display">{r.label}</span>
                <span
                  className="text-white/90 text-right truncate"
                  data-testid={`wa-intake-captured-${r.key}`}
                  data-value={String(r.value).slice(0, 80)}
                  title={String(r.value)}
                >
                  {r.value}
                </span>
              </div>
            ))}
          </div>
        )}
        {/* Always-visible "condition_score: 80 default" chip per Phase 10.1E spec —
            condition_score is never asked over WhatsApp (defaults to 80), and
            admins must be able to distinguish defaulted vs customer-entered. */}
        <div className="mt-3">
          <span
            data-testid="wa-intake-condition-score-default"
            className="inline-flex items-center gap-1.5 px-2 py-1 border text-[10px] uppercase tracking-wider font-display bg-white/[0.03] text-white/55 border-white/10"
            title="condition_score is not asked over WhatsApp intake — defaulted to 80 to match Sell.jsx slider default."
          >
            <Hourglass size={10} weight="bold" />
            condition_score: 80 default
          </span>
        </div>
      </div>

      {/* Missing required + skipped optional */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] text-white/45 tracking-widest uppercase font-display mb-2">
            Missing required ({missingRequired.length})
          </div>
          {missingRequired.length === 0 ? (
            <div className="text-emerald-400/80 text-xs" data-testid="wa-intake-missing-empty">
              All required fields captured.
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5" data-testid="wa-intake-missing-list">
              {missingRequired.map((s) => (
                <span
                  key={s.key}
                  data-testid={`wa-intake-missing-${s.key}`}
                  className={`text-[10px] uppercase tracking-wider font-display px-2 py-1 border ${
                    s.key === currentStepKey
                      ? "bg-sky-500/15 text-sky-300 border-sky-500/40 font-bold"
                      : "bg-white/[0.03] text-white/55 border-white/10"
                  }`}
                >
                  {s.label}{s.key === currentStepKey ? " · current" : ""}
                </span>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="text-[10px] text-white/45 tracking-widest uppercase font-display mb-2">
            Skipped optional ({skippedOptional.length})
          </div>
          {skippedOptional.length === 0 ? (
            <div className="text-white/45 text-xs italic" data-testid="wa-intake-skipped-empty">
              None.
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5" data-testid="wa-intake-skipped-list">
              {skippedOptional.map((s) => (
                <span
                  key={s.key}
                  data-testid={`wa-intake-skipped-${s.key}`}
                  className="text-[10px] uppercase tracking-wider font-display px-2 py-1 border bg-white/[0.03] text-white/55 border-white/10"
                >
                  {s.label}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pause / needs-review context — read-only */}
      {(fi.pause_reason || fi.needs_admin_review_reason || fi.submission_error || fi.submission_vehicle_id || fi.last_unparsed_reply) && (
        <div className="border border-amber-500/20 bg-amber-500/[0.04] p-3 text-xs space-y-1" data-testid="wa-intake-context">
          {fi.pause_reason && (
            <div><span className="text-white/55 uppercase tracking-wider text-[10px] font-display">Pause reason: </span><span className="text-amber-300/90 font-mono">{fi.pause_reason}</span></div>
          )}
          {fi.needs_admin_review_reason && (
            <div><span className="text-white/55 uppercase tracking-wider text-[10px] font-display">Review reason: </span><span className="text-amber-300/90 font-mono">{fi.needs_admin_review_reason}</span></div>
          )}
          {fi.last_unparsed_reply && (
            <div data-testid="wa-intake-last-unparsed">
              <span className="text-white/55 uppercase tracking-wider text-[10px] font-display">Last unparsed reply: </span>
              <span className="text-white/85 font-mono">"{String(fi.last_unparsed_reply).slice(0, 120)}"</span>
              {fi.last_unclear_at && (
                <span className="text-white/45 ml-2">· {relativeTime(fi.last_unclear_at)}</span>
              )}
            </div>
          )}
          {fi.submission_error && (
            <div><span className="text-white/55 uppercase tracking-wider text-[10px] font-display">Submission error: </span><span className="text-red-300/90 font-mono">{fi.submission_error}</span></div>
          )}
          {fi.submission_vehicle_id && (
            <div className="flex items-center gap-2">
              <span className="text-white/55 uppercase tracking-wider text-[10px] font-display">Vehicle ID:</span>
              <span className="text-white/85 font-mono text-[11px]">{fi.submission_vehicle_id}</span>
              <ArrowRight size={12} className="text-white/40" />
              <span className="text-white/45 text-[10px] italic">created via WhatsApp intake</span>
            </div>
          )}
        </div>
      )}

      {/* Read-only footer note explaining no write controls in this phase. */}
      <div className="text-[10px] text-white/30 font-mono pt-2 border-t border-white/5">
        Read-only. Pause / resume / stop controls are not enabled in this phase —
        use the existing CRM <span className="text-white/45">Status</span> field
        (Lost / Not Interested / Closed) to terminate automation manually.
      </div>
    </div>
  );
}

function Stat({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10px] text-white/45 tracking-widest uppercase font-display mb-0.5">{label}</div>
      <div>{children}</div>
    </div>
  );
}
